import {
  computeLocalEntityPoseFromWorldTransforms,
  RevisionTransformCache,
} from "@/data-preparation-tool/store/revision/revision-transform-cache";
import { RootState } from "@/store/store";
import { GUID } from "@faro-lotv/foundation";
import {
  CaptureTreeEntityRevision,
  CaptureTreeEntityType,
  CaptureTreeEntityTypeHint,
  CreateOrUpdateClusterEntityParams,
  CreateOrUpdateScanEntityParams,
  isSiteScapeClusterEntity,
  ProjectApi,
  RegistrationRevision,
} from "@faro-lotv/service-wires";
import type { NodeApi } from "react-arborist";
import { Matrix4 } from "three";
import { applyRevision } from "../../editing/apply-revision";
import { revisionToEdit } from "../../inspect-and-publish/refine-and-publish-utils";
import { EntityMap } from "../../store/revision/revision-slice";

/**
 * @param entity Entity to check.
 * @returns true if the entity is a special capture cluster that should not be used as a drop target.
 */
function isSpecialCaptureCluster(entity: CaptureTreeEntityRevision): boolean {
  return (
    (entity.type === CaptureTreeEntityType.cluster &&
      entity.typeHint === CaptureTreeEntityTypeHint.orbisCapture) ||
    isSiteScapeClusterEntity(entity)
  );
}

/**
 * @param scanId ID of the scan to check.
 * @param entityMap Map of all entities by ID.
 * @returns true if the scan is the child of a special capture cluster.
 */
function isScanInSpecialCluster(scanId: GUID, entityMap: EntityMap): boolean {
  const entity = entityMap[scanId];
  if (!entity || !entity.parentId) {
    return false;
  }
  const parent = entityMap[entity.parentId];
  return parent ? isSpecialCaptureCluster(parent) : false;
}

/**
 * @param entityId ID of the entity to check.
 * @param ancestorId ID of the potential ancestor.
 * @param entityMap Map of all entities by ID.
 * @returns true if entityId is a descendant of ancestorId
 */
function isDescendantOf(
  entityId: GUID,
  ancestorId: GUID,
  entityMap: EntityMap,
): boolean {
  let current = entityMap[entityId];
  while (current && current.parentId) {
    if (current.parentId === ancestorId) {
      return true;
    }
    current = entityMap[current.parentId];
  }
  return false;
}

/**
 * @param dragSources All entities currently in the drag selection.
 * @param entityMap Map of all entities by ID.
 * @returns true if the entity can be dragged given the provided drag selection.
 *   A scan inside a special capture cluster is considered acceptable when the parent
 *   cluster is also in the drag selection, because the scan will be carried along
 *   implicitly and filtered out by excludeDescendantsOfDraggedEntities.
 */
export function areAllDragSourcesValid(
  dragSources: CaptureTreeEntityRevision[],
  entityMap: EntityMap,
): boolean {
  function isDragSourceEffectivelyValid(
    dragSource: CaptureTreeEntityRevision,
  ): boolean {
    return (
      (!!dragSource.parentId &&
        !isScanInSpecialCluster(dragSource.id, entityMap)) ||
      dragSources.some((other) => other.id === dragSource.parentId)
    );
  }

  return dragSources.every((dragSource) =>
    isDragSourceEffectivelyValid(dragSource),
  );
}

/** Position among the children of the drop target at which the dragged entities are dropped. */
export type DropPosition = {
  /** IDs of the children of the drop target, in their current order. */
  siblingIds: GUID[];
  /** ID of the child to place the dragged entities before. If undefined, they are placed last. */
  beforeSiblingId?: GUID;
};

/**
 * @param siblingIds IDs of the children of the drop target, in their current order.
 * @param draggedIds IDs of the dragged entities.
 * @param index Index in siblingIds at which the dragged entities are dropped.
 * @returns The position at which the dragged entities are dropped.
 */
export function getDropPosition(
  siblingIds: GUID[],
  draggedIds: GUID[],
  index: number,
): DropPosition {
  const draggedIdSet = new Set(draggedIds);
  return {
    siblingIds,
    beforeSiblingId: siblingIds
      .slice(index)
      .find((id) => !draggedIdSet.has(id)),
  };
}

/**
 * @param position Position at which the dragged entities are dropped.
 * @param draggedIds IDs of the dragged entities.
 * @returns true if dropping at the position leaves the order of the children unchanged.
 */
function isNoOpDropPosition(
  { siblingIds, beforeSiblingId }: DropPosition,
  draggedIds: GUID[],
): boolean {
  const draggedIdSet = new Set(draggedIds);
  const movedIds = siblingIds.filter((id) => draggedIdSet.has(id));
  // Entities from other parents are dragged in, which always changes the children.
  if (movedIds.length !== draggedIds.length) return false;

  const keptIds = siblingIds.filter((id) => !draggedIdSet.has(id));
  const insertIndex = beforeSiblingId
    ? keptIds.indexOf(beforeSiblingId)
    : keptIds.length;
  const newIds = [
    ...keptIds.slice(0, insertIndex),
    ...movedIds,
    ...keptIds.slice(insertIndex),
  ];
  return newIds.every((id, index) => id === siblingIds[index]);
}

/**
 * @param dropNode Node of the drop target to check.
 * @param draggedIds IDs of the dragged entities.
 * @param entityMap Map of all entities by ID.
 * @param position Position among the children of the drop target. If undefined, the entities
 *  are dropped onto the drop target itself.
 * @returns true if the entity is a valid drop target for the dragged entities.
 */
export function isValidDropTarget(
  dropNode: NodeApi<CaptureTreeEntityRevision> | null,
  draggedIds: GUID[],
  entityMap: EntityMap,
  position?: DropPosition,
): boolean {
  if (position && isNoOpDropPosition(position, draggedIds)) {
    return false;
  }
  // Always allow dropping into the menu's root node, which doesn't correspond to any entity.
  // In this case, the dragged entities will be reparented to the root entity.
  if (
    dropNode === null ||
    dropNode.level === -1 ||
    dropNode.data.id === "__REACT_ARBORIST_INTERNAL_ROOT__"
  ) {
    return true;
  }
  const dropTarget = dropNode.data;
  // Can't drop into special capture clusters.
  // Can't drop into itself.
  if (
    isSpecialCaptureCluster(dropTarget) ||
    draggedIds.includes(dropTarget.id)
  ) {
    return false;
  }
  // Can't drop into any of the dragged entities' descendants.
  for (const draggedId of draggedIds) {
    if (isDescendantOf(dropTarget.id, draggedId, entityMap)) {
      return false;
    }
  }
  // If for all dragged entities the drop target is its current parent, then dropping onto the
  // drop target is effectively a no-op and should be disallowed.
  if (
    !position &&
    draggedIds.every((draggedId) => {
      const draggedEntity = entityMap[draggedId];
      return draggedEntity?.parentId === dropTarget.id;
    })
  ) {
    return false;
  }

  return true;
}

/**
 * @param draggedEntities Dragged entities.
 * @param entityMap Map of all entities by ID.
 * @returns Dragged entities with all those removed that are descendants of other dragged entities.
 */
export function excludeDescendantsOfDraggedEntities(
  draggedEntities: CaptureTreeEntityRevision[],
  entityMap: EntityMap,
): CaptureTreeEntityRevision[] {
  const draggedIds = new Set(draggedEntities.map((entity) => entity.id));

  function hasDraggedAncestor(entity: CaptureTreeEntityRevision): boolean {
    let parent = entity.parentId ? entityMap[entity.parentId] : undefined;
    while (parent) {
      if (draggedIds.has(parent.id)) return true;
      parent = parent.parentId ? entityMap[parent.parentId] : undefined;
    }
    return false;
  }

  return draggedEntities.filter((entity) => !hasDraggedAncestor(entity));
}

/**
 * @param entities Entities to sort.
 * @param topLevelEntities The entities at the top level of the tree, in their order.
 * @param entityChildrenMap The children of the entities by parent ID, in their order.
 * @returns The entities in the order they are shown in the tree.
 */
export function sortByTreeOrder(
  entities: CaptureTreeEntityRevision[],
  topLevelEntities: CaptureTreeEntityRevision[],
  entityChildrenMap: Map<GUID, CaptureTreeEntityRevision[]>,
): CaptureTreeEntityRevision[] {
  const ids = new Set(entities.map((entity) => entity.id));
  const sorted: CaptureTreeEntityRevision[] = [];

  function visit(siblings: CaptureTreeEntityRevision[]): void {
    for (const entity of siblings) {
      if (ids.has(entity.id)) sorted.push(entity);
      visit(entityChildrenMap.get(entity.id) ?? []);
    }
  }
  visit(topLevelEntities);

  return sorted;
}

/**
 * Calculates new local poses for the dragged entities when moving to the new parent.
 *
 * @param dropTargetId ID of the drop target, the new parent.
 * @param draggedIds IDs of the dragged entities.
 * @param transformCache World transform cache delivered by selectDataPreparationTransformCache.
 * @returns The new local poses and world transform cache for the affected entities.
 *   null if there is a problem and the move should be aborted.
 */
export function calculateNewPosesForMove(
  dropTargetId: GUID,
  draggedIds: GUID[],
  transformCache: RevisionTransformCache,
): Record<GUID, CaptureTreeEntityRevision["pose"] | undefined> | null {
  const worldTransformOfDropTarget = transformCache[dropTargetId];
  if (!worldTransformOfDropTarget) {
    return null;
  }
  const mat4OfDropTarget = new Matrix4().fromArray(
    worldTransformOfDropTarget.worldMatrix,
  );

  const newPoses: Record<GUID, CaptureTreeEntityRevision["pose"] | undefined> =
    {};

  for (const draggedId of draggedIds) {
    const worldTransformOfDraggedEntity = transformCache[draggedId];
    if (!worldTransformOfDraggedEntity) {
      return null;
    }
    const mat4OfDraggedEntity = new Matrix4().fromArray(
      worldTransformOfDraggedEntity.worldMatrix,
    );

    newPoses[draggedId] = computeLocalEntityPoseFromWorldTransforms(
      mat4OfDraggedEntity,
      mat4OfDropTarget,
    );
  }

  return newPoses;
}

type SaveMovedEntitiesParams = {
  /** The Project API client used to persist the changes. */
  projectApiClient: ProjectApi;
  /** The current application state. */
  state: RootState;
  /** The revision the moved entities belong to. */
  revision: RegistrationRevision;
  /** The entities that were moved to the new parent. */
  movedEntities: CaptureTreeEntityRevision[];
  /** The ID of the new parent the entities were moved to. */
  newParentId: GUID;
  /** The ID of the sibling the entities were placed before. null places them last. */
  beforeSiblingId: GUID | null;
  /** The new local poses of the moved entities, keyed by entity ID. */
  newPoses: Record<GUID, CaptureTreeEntityRevision["pose"] | undefined>;
};

/**
 * Persists a move of clusters and scans to a new parent in the Draft view's scan tree, using the
 * same approach as for renaming entities in renameEntity.
 *
 * @param params Parameters describing the move.
 */
export async function saveMovedEntities({
  projectApiClient,
  state,
  revision,
  movedEntities,
  newParentId,
  beforeSiblingId,
  newPoses,
}: SaveMovedEntitiesParams): Promise<void> {
  const editRevision = await revisionToEdit(revision, projectApiClient, state);

  // The backend places the entities of a request before the sibling one after another. Clusters
  // and scans need separate requests, so consecutive runs of them are saved in order to keep
  // the order of the moved entities.
  type UpdatedEntity = CreateOrUpdateClusterEntityParams &
    CreateOrUpdateScanEntityParams;
  const runs: Array<{ isCluster: boolean; requestBody: UpdatedEntity[] }> = [];

  for (const entity of movedEntities) {
    const isCluster = entity.type === CaptureTreeEntityType.cluster;
    const updatedEntity: UpdatedEntity = {
      ...entity,
      parentId: newParentId,
      pose: newPoses[entity.id] ?? entity.pose,
      beforeSibling: beforeSiblingId,
    };

    const lastRun = runs.at(-1);
    if (lastRun?.isCluster === isCluster) {
      lastRun.requestBody.push(updatedEntity);
    } else {
      runs.push({ isCluster, requestBody: [updatedEntity] });
    }
  }

  for (const { isCluster, requestBody } of runs) {
    const params = { registrationRevisionId: editRevision.id, requestBody };
    if (isCluster) {
      await projectApiClient.createOrUpdateClusterEntitiesForRegistrationRevision(
        params,
      );
    } else {
      await projectApiClient.createOrUpdateScanEntitiesForRegistrationRevision(
        params,
      );
    }
  }

  await applyRevision({ revision: editRevision, projectApiClient });
}
